<template>
  <div data-aos="fade-up" class="container justify-content-center">
    <div
      class="card col-xl-8"
      :class="{
        'card-dark': isDarkMode,
        'card-light': !isDarkMode,
        'card-left-padding': !showImageRight,
      }">
      <CardImage
        v-if="!showImageRight"
        :is-right="showImageRight"
        :image-source="imageSource"
        :is-small-image="isSmallImage"
        :is-large-image="isLargeImage" />
      <div class="col-md-6">
        <p class="title">{{ title }}</p>
        <p class="description">
          {{ description }}
        </p>
        <div v-if="links">
          <a
            v-for="(link, index) in links"
            :key="index"
            :href="link"
            target="_blank"
            rel="noopener noreferrer"
            class="link no-decorative-link">
            {{ link }}
          </a>
        </div>
        <div v-if="buttons" class="d-flex flex-row gap-2 mt-3">
          <a
            v-for="(button, index) in buttons"
            :key="index"
            :href="button.link"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-custom btn-sm"
            :class="{
              'btn-outline-dark-custom': isDarkMode,
              'btn-outline-light-custom': !isDarkMode,
            }">
            {{ button.text }}
          </a>
        </div>
        <div class="tech-stack">
          <div v-for="(tech, index) in techStack" :key="index">
            <div
              class="tech-item"
              :class="{
                'bg-black': isDarkMode,
                'bg-secondary': !isDarkMode,
              }">
              {{ tech }}
            </div>
          </div>
        </div>
      </div>
      <CardImage
        v-if="showImageRight"
        :is-right="showImageRight"
        :image-source="imageSource"
        :is-small-image="isSmallImage"
        :is-large-image="isLargeImage" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {defineProps, ref, watch} from 'vue';
import CardImage from './CardImage.vue';
import {useStore} from 'vuex';

defineProps<{
  title: string;
  description: string;
  links?: string[];
  buttons?: {
    text: string;
    link: string;
  }[];
  techStack: string[];
  imageSource: string;
  showImageRight: boolean;
  isSmallImage: boolean;
  isLargeImage: boolean;
}>();

const store = useStore();
const isDarkMode = ref(store.state.isDarkMode);

watch(
  () => store.state.isDarkMode,
  newValue => {
    isDarkMode.value = newValue;
  },
);
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  justify-content: center;
}

.card {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 10px;
  padding: 40px 0 0 40px;
  position: relative;
  overflow: hidden;

  .col-6 {
    width: 50%;
    position: relative;
  }

  .description {
    font-size: 1rem;
  }

  .link {
    font-size: 1rem;
    display: block;
  }

  .link-mt {
    margin-top: 10px;
  }

  .btn-custom {
    &.btn-outline-dark-custom {
      color: #ccc;
      border-color: #ccc;
    }
    &.btn-outline-light-custom {
      color: #000000;
      border-color: #000000;
    }
    &:hover {
      color: white;
      background-color: #000000;
      border-color: #000000;
    }
  }

  .tech-stack {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 20px 0;

    .tech-item {
      color: #ffffffd3;
      font-size: 0.7rem;
      padding: 5px 10px;
      border-radius: 8px;
    }
  }
}

.card-dark {
  background-color: var(--dark-mode-secondary-bg);
  color: var(--dark-mode-text);

  .description {
    color: #ffffffb2;
  }

  &:hover {
    background-color: var(--dark-mode-secondary-hover-bg);
  }
}

.card-light {
  background-color: var(--light-mode-secondary-bg);
  color: var(--light-mode-text);

  &:hover {
    background-color: var(--light-mode-secondary-hover-bg);
  }
}

.card-left-padding {
  padding: 40px 40px 0 0;
}

@media screen and (max-width: 768px) {
  .card {
    padding: 20px;
  }
}
</style>
